<script setup lang="ts">
    import { computed, ref, shallowRef, useHost, watch } from 'vue'
    import { hostToolFromPage, type RegisteredHostTool } from 'composables'
    import { usePageTools } from '~/composables/usePageTools'
    import { usePagePresence } from '~/composables/usePagePresence'
    import { usePagePointer } from '~/composables/usePagePointer'
    import { useI18n } from 'vue-i18n'
    import { useChatbotAuth } from '~/composables/useChatbotAuth'
    import { useChatbotProfileProvider } from '~/composables/useChatbotProfileProvider'
    import { useChatbotAgent } from '~/composables/useChatbotAgent'
    import { useChatbotAuthForm } from '~/composables/useChatbotAuthForm'
    import { useChatbotStoreSync } from '~/composables/useChatbotStoreSync'
    import { useFocusTrapBypass } from '~/composables/useFocusTrapBypass'
    import { usePendingMessage } from '~/composables/usePendingMessage'
    import { DEFAULT_BACKEND_URL } from '~/utils'
    import {
        initializeTelemetry,
        setTelemetryContext,
        trackAppException,
    } from '~/modules/applicationInsights'
    import PkChatbot from 'components/src/chat/PkChatbot.vue'
    import PkPageControlBar from 'components/src/chat/PkPageControlBar.vue'
    import PkPagePointer from 'components/src/chat/PkPagePointer.vue'
    import PkAgentPresence from 'components/src/chat/PkAgentPresence.vue'
    import { isAchromaticColor } from 'components/src/chat/utils'
    import PkHostCallConfirmDialog from 'components/src/chat/PkHostCallConfirmDialog.vue'
    import {
        usePageModal,
        usePageModalDock,
        usePageModalStop,
    } from '~/composables/usePageModal'
    import PkChatbotAuth from 'components/src/chat/PkChatbotAuth.vue'
    import PkLoader from 'components/src/PkLoader.vue'
    import { useDialog } from 'composables'
    import {
        useWidgetEvents,
        usePageControlSignal,
        useWorkingSignal,
    } from '~/utils/emitWidgetEvent'

    defineOptions({
        inheritAttrs: false,
    })

    useFocusTrapBypass()

    const props = withDefaults(
        defineProps<{
            agentId: string
            organizationSlug: string
            /** The backend that hosts the agent: the Eight Wave cloud unless a dedicated installation sets its own. */
            baseUrl?: string
            externalToken?: string
            /** Boolean or attribute string ("true"/"false") when set via HTML attribute. */
            awaitExternalToken?: boolean | string
            /**
             * Host-session mode: the widget skips its own sign-in and uses
             * the host platform's better-auth session from the cookie.
             * Boolean or attribute string ("true"/"false").
             */
            useHostSession?: boolean | string
            /** Comma-separated list of extra slot names to forward to PkChatbot (e.g. "tool-showArtifact,subagent"). */
            forwardedSlots?: string
            customCss?: string
            /** Override the session/anonymous cookie name (defaults to 'eight-wave-ai'). */
            cookieName?: string
            /** Layout mode: `widget` (default, contained with header), `fullscreen` (full-page app layout) or `panel` (compact chrome for a docked side panel; the host owns background, border and open/close). */
            displayMode?: 'widget' | 'fullscreen' | 'panel'
            /**
             * Force the chat color scheme instead of following the OS
             * `prefers-color-scheme`. Let a host app hand down its own theme.
             */
            systemTheme?: 'light' | 'dark'
            /**
             * The chat's color scheme, over the agent's own setting too: a
             * host that switches between light and dark around the chat
             * (Agent Studio's assistant) keeps the chat in step with it.
             */
            theme?: 'light' | 'dark'
            /**
             * While the page has a modal dialog open, the chat moves into it
             * and stays usable, where the rest of the page is inert.
             * Boolean or attribute string ("true"/"false").
             */
            followPageDialogs?: boolean | string
            /**
             * The chat's menu offers "Close", which dispatches
             * `close-requested`: the host closes the panel it opened.
             * Boolean or attribute string ("true"/"false").
             */
            closable?: boolean | string
        }>(),
        {
            baseUrl: DEFAULT_BACKEND_URL,
            externalToken: undefined,
            awaitExternalToken: false,
            useHostSession: false,
            forwardedSlots: undefined,
            customCss: undefined,
            cookieName: undefined,
            displayMode: 'widget',
            systemTheme: undefined,
            theme: undefined,
            followPageDialogs: false,
            closable: false,
        },
    )

    // An empty attribute counts as none: requests must never go to the page's
    // own origin.
    const baseUrl = props.baseUrl || DEFAULT_BACKEND_URL

    // dialog
    const { PkGlobalDialog } = useDialog()

    // Widget events (must be called during setup to capture the component instance)
    const { emit: emitWidgetEvent } = useWidgetEvents()

    // chatbot ref
    const chatbotEl = ref<InstanceType<typeof PkChatbot>>()

    // external context set by host application
    const externalContext = ref<Record<string, unknown> | undefined>()

    // tools the host page registered and runs itself (spec host-tools.md)
    const hostToolRegistry = shallowRef<RegisteredHostTool[]>([])

    setTelemetryContext({
        organizationSlug: props.organizationSlug,
        agentId: props.agentId,
    })

    const {
        session,
        onError: onAuthError,
        headers,
        token,
        userId,
        isExternalAuth,
        signInWithEmail,
        twoFactorChallenge,
        verifyTwoFactor,
        sendTwoFactorCode,
        cancelTwoFactor,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        signInWithExternalToken,
        signOut,
    } = useChatbotAuth({
        baseUrl,
        externalToken: () => props.externalToken,
        awaitExternalToken:
            !!props.awaitExternalToken && props.awaitExternalToken !== 'false',
        useHostSession: () =>
            !!props.useHostSession && props.useHostSession !== 'false',
        cookieName: props.cookieName,
        onSignedIn: (detail) => emitWidgetEvent('signed-in', detail),
        onSignedOut: () => emitWidgetEvent('signed-out'),
        onExternalAuthExpired: () => emitWidgetEvent('external-auth-expired'),
        onExternalTokenExpiring: () =>
            emitWidgetEvent('external-token-expiring'),
    })

    // Enrich telemetry with userId once auth completes
    watch(userId, (id) => {
        if (id) {
            setTelemetryContext({ userId: id })
        }
    })

    // The chat store outlives PkChatbot (v-if'd on the session): keep the
    // session identity mirrored into it so the identity-switch reset starts
    // as soon as the new identity is known.
    const chatStore = useChatbotStoreSync({
        agentId: props.agentId,
        baseUrl,
        headers,
        userId,
    })

    // the `working` attribute and events, for the host page (spec widget-copilot.md)
    useWorkingSignal(() => chatStore.isWorking)
    usePageControlSignal(() => chatStore.pageControlActive)

    // a message the host page sent before the chat could take it
    const pendingMessage = usePendingMessage(
        chatbotEl,
        () => chatStore.localChatId,
    )

    // emit 'ready' once per signed-in user: a refetch of the same session
    // is not news, another user is
    watch(userId, (id) => {
        if (id) {
            emitWidgetEvent('ready')
        }
    })

    function onError(error: { type: string; title: string; status: number }) {
        trackAppException(new Error(`${error.type}: ${error.title}`), {
            status: String(error.status),
        })
        emitWidgetEvent('chat-error', {
            type: error.type,
            title: error.title,
            status: error.status,
        })
        onAuthError(error)
    }

    // i18n
    const { t } = useI18n({ useScope: 'global' })

    // A modal dialog of the page makes the chat inert: the card the agent
    // waits on moves to the top layer, and the stop goes inside that dialog
    // (spec page-tools.md). With `follow-page-dialogs` the chat moves into
    // that dialog instead, and once it takes clicks there the card stays in
    // the chat and the bar's stop is reachable (spec
    // page-tools-improvements.md, M11)
    const pageModal = usePageModal(
        () => !!chatStore.hostConfirmation || chatStore.pageControlActive,
    )
    const chatInPageModal = usePageModalDock(
        useHost(),
        () => !!props.followPageDialogs && props.followPageDialogs !== 'false',
    )
    usePageModalStop(
        pageModal,
        () => chatStore.pageControlActive && !chatInPageModal.value,
        {
            text: () => t('messagePart.pageControlActive'),
            stopLabel: () => t('action.stop'),
            onStop: () => void chatStore.stopHostActions(),
        },
    )

    const {
        agent,
        branding,
        authMethods,
        deploymentCss,
        deployment,
        requiresAuth,
        notFound,
        isLoading,
        reload,
        themeClass,
        themeStyle,
    } = useChatbotAgent({
        baseUrl,
        organizationSlug: () => props.organizationSlug,
        agentId: () => props.agentId,
        headers,
        systemTheme: () => props.systemTheme,
        theme: () => props.theme,
    })
    // The chat reads its scheme from the agent's interface: the host's wins
    const chatInterface = computed(() =>
        props.theme && agent.value
            ? { ...agent.value.interface, theme: props.theme }
            : agent.value?.interface,
    )

    // browser telemetry, as the installation asks: none until it says so
    watch(deployment, (current) => {
        if (current?.telemetry) {
            initializeTelemetry({ backendUrl: baseUrl, ...current.telemetry })
        }
    })

    // page tools of the widget (spec page-tools.md), and the cursor that
    // shows where they act (spec page-tools-improvements.md, M12)
    const presence = usePagePresence(() => chatStore.pageControlActive)
    const {
        registry: pageToolRegistry,
        forms: pageForms,
        registerPages,
        pointing,
    } = usePageTools(() => agent.value?.capabilities?.pageAccess, t, presence)
    const presenceTarget = presence.target

    // What the user points at on the page for the next message (spec
    // page-point-and-choose.md, P): the chat is part of the page, always open
    const pagePointer = usePagePointer({
        pointing,
        // Not while an answer runs, as the input's own button
        available: () => chatStore.pagePointerAvailable && !chatStore.isWorking,
        chatOpen: () => true,
        onPointed: (pointer) => {
            chatStore.pagePointer = pointer
        },
    })
    chatStore.pagePointerStart = pagePointer.start
    watch(pagePointer.picking, (picking) => {
        chatStore.pagePointing = picking
    })
    const presenceHome = () => chatbotEl.value?.$el as Element | undefined
    // A gray agent color (the platform's own agent is black) draws a dark
    // ring: the accent lights the cursor instead, as the beam in Agent Studio
    const presenceStyle = computed(() =>
        isAchromaticColor(agent.value?.interface.mainColor)
            ? { '--pk-agent-presence-color': 'var(--color-accent)' }
            : undefined,
    )

    // Read-only profile context + voluntary sign-in affordance shared by both
    // widgets. Provides ChatbotProfileContextKey for the chat components.
    const { authBranding } = useChatbotProfileProvider({
        agent,
        branding,
        authMethods,
        session,
        signOut,
        isExternalAuth,
        frontendUrl: () => deployment.value?.frontendUrl,
        token,
        baseUrl,
        headers,
        cookieName: props.cookieName,
    })

    // Login screen (private agents and the voluntary `view-auth` slot)
    const {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
        handleVerifyTwoFactor,
        handleSendTwoFactorCode,
        handleCancelTwoFactor,
    } = useChatbotAuthForm({
        signInWithEmail,
        isTwoFactorPending: () => !!twoFactorChallenge.value,
        verifyTwoFactor,
        sendTwoFactorCode,
        cancelTwoFactor,
        signUpWithEmail,
        requestPasswordReset,
        frontendUrl: () => deployment.value?.frontendUrl,
        signInWithSocialPopup,
        refetchSession: () => session.value.refetch(),
        reload,
    })

    const extraSlotNames = computed(() =>
        props.forwardedSlots
            ? props.forwardedSlots
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [],
    )

    // Greeting personalization: only real (non-anonymous) names
    const userName = computed(() =>
        session.value.data?.user?.isAnonymous
            ? undefined
            : session.value.data?.user?.name,
    )

    const mergedCss = computed(() => {
        const parts = [
            agent.value?.interface?.customCss,
            branding.value?.interface.customCss,
            props.customCss,
        ].filter(Boolean)
        return parts.length ? parts.join('\n') : undefined
    })

    // Public API exposed on the custom element DOM node
    defineExpose({
        sendMessage(text: string) {
            pendingMessage.send(text)
        },
        startNewChat() {
            pendingMessage.invalidate()
            void chatStore.startNewChat()
        },
        setContext(context: Record<string, unknown>) {
            externalContext.value = context
        },
        /** Same shape as WebMCP's `navigator.modelContext.registerTool`; a name registered again replaces the old tool. */
        registerTool(tool: RegisteredHostTool) {
            hostToolRegistry.value = [
                ...hostToolRegistry.value.filter(
                    ({ name }) => name !== tool.name,
                ),
                hostToolFromPage(tool),
            ]
        },
        unregisterTool(name: string) {
            hostToolRegistry.value = hostToolRegistry.value.filter(
                (tool) => tool.name !== name,
            )
        },
        /** The pages of the site the agent may open without asking (spec `page-tools-improvements.md`, M6); each call replaces the list. */
        registerPages,
        signIn: signInWithExternalToken,
        signOut,
        get isWorking() {
            return chatStore.isWorking
        },
        get isControllingPage() {
            return chatStore.pageControlActive
        },
    })
</script>

<template>
    <div
        class="embedded-chat-widget-root"
        :class="themeClass"
        :style="themeStyle">
        <!-- #region styles -->
        <!-- The deployment theme in a sheet of its own, before the agent's
             CSS: what follows it wins, and an @import at the top of the
             agent's CSS stays at the top of its sheet. -->
        <component :is="'style'" v-if="deploymentCss">{{
            deploymentCss
        }}</component>
        <component :is="'style'" v-if="mergedCss">{{ mergedCss }}</component>
        <!-- #endregion -->

        <!-- #region page control bar: the agent acting on the page, and the
             user's stop (spec page-tools.md) -->
        <PkAgentPresence
            :active="chatStore.pageControlActive"
            :target="presenceTarget"
            :agent-name="agent?.name"
            :home="presenceHome"
            :above="pageModal"
            :style="presenceStyle"
            @arrived="presence.arrived" />
        <PkPageControlBar
            :active="chatStore.pageControlActive"
            :action="chatStore.pageAction?.title"
            :chat="chatbotEl?.$el"
            @stop="chatStore.stopHostActions()" />
        <PkPagePointer
            :picking="pagePointer.picking.value"
            :box="pagePointer.box.value"
            :bubble="pagePointer.bubble.value"
            :style="presenceStyle"
            @cancel="pagePointer.stop()"
            @ask="pagePointer.ask()"
            @move="pagePointer.move"
            @press="pagePointer.press" />
        <PkHostCallConfirmDialog
            v-if="chatStore.hostConfirmation && pageModal && !chatInPageModal"
            :question="
                chatStore.hostConfirmation.question ?? t('toolApproval.prompt')
            "
            :details="chatStore.hostConfirmation.details"
            :unchanged="chatStore.hostConfirmation.unchanged"
            @respond="chatStore.respondHostConfirmation" />
        <!-- #endregion -->

        <!-- #region not found -->
        <div
            v-if="notFound"
            class="embedded-chat-widget embedded-chat-widget--not-found">
            <p>{{ t('message.agentNotFound') }}</p>
        </div>
        <!-- #endregion -->

        <!-- #region auth -->
        <Transition name="fade" appear>
            <div
                v-if="
                    (agent || branding) &&
                    !notFound &&
                    requiresAuth &&
                    (!session.data?.user?.id || session.data?.user?.isAnonymous)
                "
                class="embedded-chat-widget embedded-chat-widget--auth">
                <PkChatbotAuth
                    v-if="authMethods"
                    v-model:error-message="authError"
                    v-model:success="authSuccess"
                    :branding="branding"
                    :auth-methods="authMethods"
                    :loading="authLoading"
                    :two-factor-methods="twoFactorChallenge?.methods"
                    @sign-in="handleSignIn"
                    @sign-up="handleSignUp"
                    @request-reset="handleReset"
                    @social="handleSocial"
                    @verify-two-factor="handleVerifyTwoFactor"
                    @send-two-factor-code="handleSendTwoFactorCode"
                    @cancel-two-factor="handleCancelTwoFactor" />
                <PkLoader v-else />
            </div>
        </Transition>
        <!-- #endregion -->

        <!-- #region loading -->
        <Transition name="fade" appear>
            <div
                v-if="isLoading && !notFound"
                class="embedded-chat-widget embedded-chat-widget--loading">
                <PkLoader />
            </div>
        </Transition>
        <!-- #endregion -->

        <!-- #region chatbot -->
        <PkChatbot
            v-if="agent && session.data && !notFound"
            ref="chatbotEl"
            class="embedded-chat-widget"
            :base-url
            :agent-id
            :user-id
            :name="agent.name"
            :agent-interface="chatInterface"
            :agent-file-upload="agent.capabilities?.fileUpload"
            :voice="agent.capabilities?.voice"
            :modifier="displayMode"
            :user-name="userName"
            :headers
            :system-theme="systemTheme"
            :actions="['upvote', 'downvote', 'feedback', 'regenerate']"
            :external-context="externalContext"
            :host-tools="agent.capabilities?.hostTools"
            :host-tool-registry="hostToolRegistry"
            :page-access="agent.capabilities?.pageAccess"
            :page-tool-registry="pageToolRegistry"
            :page-forms="pageForms"
            :closable="!!closable && closable !== 'false'"
            @error="onError"
            @loaded="pendingMessage.onLoaded"
            @close="emitWidgetEvent('close-requested')">
            <template #header-actions>
                <slot name="header-actions" />
            </template>
            <template #actions>
                <slot name="actions" />
            </template>
            <!-- Voluntary sign-in view for anonymous users (chat dropdown) -->
            <template #view-auth>
                <PkChatbotAuth
                    v-model:error-message="authError"
                    v-model:success="authSuccess"
                    :branding="authBranding"
                    :auth-methods="authMethods"
                    :loading="authLoading"
                    :two-factor-methods="twoFactorChallenge?.methods"
                    @sign-in="handleSignIn"
                    @sign-up="handleSignUp"
                    @request-reset="handleReset"
                    @social="handleSocial"
                    @verify-two-factor="handleVerifyTwoFactor"
                    @send-two-factor-code="handleSendTwoFactorCode"
                    @cancel-two-factor="handleCancelTwoFactor" />
            </template>
            <template v-for="name in extraSlotNames" #[name]>
                <slot :name="name" />
            </template>
        </PkChatbot>
        <!-- #endregion -->

        <!-- #region global dialogs -->
        <PkGlobalDialog />
        <!-- #endregion -->
    </div>
</template>

<style lang="scss">
    @use '~/assets/scss/style';

    .embedded-chat-widget-root {
        display: contents;
    }

    .embedded-chat-widget {
        width: 100%;
        height: 100%;

        &--loading,
        &--not-found {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &--auth {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
</style>
